import React, { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './collapsible';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CollapseProps {
  variant?: 'outer' | 'inner' | 'nested';
  left?: React.ReactNode;
  title: React.ReactNode;
  right?: React.ReactNode;
  defaultOpen?: boolean;
  contentClassName?: string;
  children: React.ReactNode;
}

export function Collapse({ variant = 'inner', left, title, right, defaultOpen = false, contentClassName, children }: CollapseProps) {
  const [open, setOpen] = useState(defaultOpen);

  const containerClasses = cn(
    "overflow-hidden transition-all",
    variant === 'outer' && "rounded-lg border bg-card text-card-foreground shadow-sm",
    variant === 'inner' && "rounded-md border border-border bg-muted/30",
    variant === 'nested' && "rounded-sm bg-background border"
  );

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={containerClasses}>
      <CollapsibleTrigger asChild>
        <button type="button" className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {left && <div className="shrink-0">{left}</div>}
            <div className="text-sm font-medium truncate">{title}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ms-2">
            {right && <div onClick={e => e.stopPropagation()}>{right}</div>}
            <ChevronDown className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200", open && "rotate-180")} />
          </div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        <div className={cn("p-3 pt-0", contentClassName)}>
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
